/**
 * v3.21-1776 · Multi-recipient Financing Result Team Message composer.
 *
 * Extends v3.21-1764 with:
 *   • multi-select recipient checkboxes (authoritative Deal recipients)
 *   • Select All (limited to authoritative recipients only)
 *   • "Use Different Number" one-off manual destination preserved
 *   • per-recipient result summary (Sent / Failed / Duplicate)
 *   • sends via POST /api/finturf/text-result/batch
 *   • VALID_OFFER (Recommended #1 / #2 via `pickTopTwoRecommendations`
 *     SSOT), NO_OFFER_FOUND, and SUBMISSION_ERROR all sendable
 *   • NEEDS_ATTENTION distinct (non-sendable through this composer)
 *   • state-aware universal title "Text Financing Result to Team"
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, MessageSquare, Send, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { pickTopTwoRecommendations, resolveRequestedAmount } from '../lib/offerRanking';

const fmtMoney  = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtMoneyD = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtApr    = (n) => `${Number(n || 0).toFixed(2)}%`;

const buildValidOfferMessage = (client, item, applicationId, requested) => {
  const lines = [
    `PTC Finance — Approved`,
    `Client: ${client?.full_name || ''}`,
    applicationId ? `Application: ${applicationId}` : null,
    `Approved: ${fmtMoney(item?.approved)}`,
    item?.term    > 0 ? `Term: ${item.term} months`                       : null,
    item?.apr     > 0 ? `APR: ${fmtApr(item.apr)}`                        : null,
    item?.payment > 0 ? `Monthly Payment: ${fmtMoneyD(item.payment)}/mo`  : null,
    requested     > 0 ? `Requested: ${fmtMoney(requested)}`               : null,
  ].filter(Boolean);
  return lines.join('\n');
};

const buildNoOfferMessage = (client, applicationId) =>
  [
    `PTC Finance Result: No Offer Found`,
    `Client: ${client?.full_name || ''}`,
    applicationId ? `Application: ${applicationId}` : null,
    `The application completed lender evaluation and no offer was returned.`,
  ].filter(Boolean).join('\n');

const buildSubmissionIssueMessage = (client, applicationId) =>
  [
    `PTC Finance Result: Submission Issue`,
    `Client: ${client?.full_name || ''}`,
    applicationId ? `Application: ${applicationId}` : null,
    `PTC Finance processing could not be completed. The application requires review.`,
  ].filter(Boolean).join('\n');

// v3.21-1796 · Provider-neutral prepopulated messages for the remaining
// canonical classifier states.  The operator can edit any of these
// before sending; the `messageEdited` guard preserves manual edits
// across incidental rerenders.
const buildProcessingMessage = (client, applicationId) =>
  [
    `PTC Finance Result: Processing`,
    `Client: ${client?.full_name || ''}`,
    applicationId ? `Application: ${applicationId}` : null,
    `The financing application is still being processed. We will update you when the result is available.`,
  ].filter(Boolean).join('\n');

const buildNotSubmittedMessage = (client, applicationId) =>
  [
    `PTC Finance Result: Not Submitted`,
    `Client: ${client?.full_name || ''}`,
    applicationId ? `Application: ${applicationId}` : null,
    `The financing application has not yet been submitted for this client.`,
  ].filter(Boolean).join('\n');

const buildNeedsAttentionMessage = (client, applicationId) =>
  [
    `PTC Finance Result: Needs Attention`,
    `Client: ${client?.full_name || ''}`,
    applicationId ? `Application: ${applicationId}` : null,
    `The current financing application needs additional information before results can be finalised.`,
  ].filter(Boolean).join('\n');

const maskPhone = (p) => {
  if (!p) return '—';
  const d = String(p).replace(/\D/g, '');
  if (d.length < 4) return p;
  return `(***) ***-${d.slice(-4)}`;
};

export default function FinancingResultTeamMessageModal({
  open,
  onOpenChange,
  client,
  // v3.21-1781 · Optional initial context supplied by the caller so the
  // modal never has to re-derive the current financing result from a
  // secondary lookup when the user clicked "Text Offer to Agent" on an
  // already-visible offer.  When the caller is DealDashboard's
  // per-offer Text button, these carry the exact clicked offer, its
  // recommended-rank position, the resolved application identifier,
  // and the parent Deal id — hydrating the composer directly.  Legacy
  // callers that omit these props keep the v1776 fetch behaviour.
  initialOffer = null,
  initialOfferNumber = null,
  initialApplicationId = null,
  initialResultType = null,
  dealId = null,
}) {
  const [loading, setLoading]       = useState(false);
  const [sending, setSending]       = useState(false);
  const [recipients, setRecipients] = useState([]);          // authoritative
  const [selectedUids, setSelectedUids] = useState([]);       // multi-select
  const [manualPhone, setManualPhone] = useState('');
  const [includeManual, setIncludeManual] = useState(false);
  const [resultType, setResultType]   = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [offers, setOffers]           = useState([]);
  const [requested, setRequested]     = useState(0);
  const [chosenRank, setChosenRank]   = useState(1);
  const [message, setMessage]         = useState('');
  const [needsAttentionFields, setNeedsAttentionFields] = useState([]);
  const [perRecipientResult, setPerRecipientResult] = useState(null);
  // v3.21-1820 · CUSTOM MESSAGE MODE.
  //
  // When ON, an authorized operator can send any non-empty custom
  // team message regardless of financing classification
  // (VALID_OFFER, NO_OFFER_FOUND, PROCESSING, SUBMISSION_ERROR,
  // NEEDS_ATTENTION, NOT_SUBMITTED, UNKNOWN, missing, or
  // contradictory).  Custom messaging NEVER depends on chosenOffer.
  //
  // When OFF (Auto mode), existing financing-specific requirements
  // for automatically generated offer messages are preserved
  // exactly as v3.21-1819 shipped them.
  const [customMessageMode, setCustomMessageMode] = useState(false);

  const clientId = client?.id;

  useEffect(() => {
    if (!open || !clientId) return;
    let cancelled = false;
    setLoading(true);
    setPerRecipientResult(null);
    (async () => {
      try {
        // v3.21-1781 · If the caller passed an already-visible offer +
        // application identity (DealDashboard's per-offer "Text Offer
        // to Agent" button always does), hydrate state directly and
        // skip the redundant `/finturf/applications/*/offers` fetch —
        // the offer the user just clicked IS the current result.  A
        // fetch failure on a valid-offer surface used to raise the
        // "Could not load current financing result" toast and disable
        // Send even though the offer was right there.
        //
        // v3.21-1782 · Application-identity resolution here now uses
        // ONLY the canonical Finturf pointers
        // (`current_finturf_application_id` and `deal_number`).  The
        // legacy `client_external_id` fallback was removed because
        // v1781 introduced Business Client ID as an operator-editable
        // field on the SAME `clients.client_external_id` — the two
        // must not collide with financing identity resolution.
        const hydratedFromCaller =
          !!(initialOffer && (initialApplicationId || client?.current_finturf_application_id));
        if (hydratedFromCaller) {
          const _appId =
            initialApplicationId
            || client?.current_finturf_application_id
            || client?.deal_number
            || null;
          const _requested = Number(initialOffer.applied_for) || resolveRequestedAmount(client);  // v3.21-1838
          const _resultType = initialResultType || 'VALID_OFFER';
          // v3.21-1787 · P0 FINANCING TEAM TEXT BLANK-MESSAGE FIX.
          //
          // Root cause: `pickTopTwoRecommendations` normalizes offers
          // via `normalizeOffer` (offerRanking.js) which reads the
          // *raw* Finturf field names (`approved_amount`, `term`,
          // `apr` OR `rate`, `monthly_payment` OR `payment`).  The
          // previous hydration produced a shape with `approved`,
          // `payment`, etc., so `normalizeOffer` saw all zeros and
          // `buildQualifyingPool` filtered the clicked offer out.
          // Result: `recommendations = []` → `chosenOffer = null` →
          // the VALID_OFFER `useEffect` never generated the message,
          // leaving the textarea BLANK while State said VALID_OFFER.
          //
          // Fix: seed `offers` with the RAW clicked-offer field names
          // that `normalizeOffer` recognizes so the qualifying pool
          // includes the exact clicked offer, `chosenOffer` resolves,
          // and the message builder fires on first render.
          const _rawOffer = {
            approved_amount: Number(initialOffer.approved_amount || initialOffer.approved || 0),
            term:            Number(initialOffer.term || 0),
            apr:             Number(initialOffer.apr || initialOffer.rate || 0),
            rate:            Number(initialOffer.rate || initialOffer.apr || 0),
            monthly_payment: Number(initialOffer.monthly_payment || initialOffer.payment || 0),
            payment:         Number(initialOffer.monthly_payment || initialOffer.payment || 0),
            offer_id:        initialOffer.offer_id || initialOffer.id || null,
            applied_for:     Number(initialOffer.applied_for || initialOffer.deal_amount || _requested || 0),
            deal_amount:     Number(initialOffer.deal_amount || initialOffer.applied_for || _requested || 0),
          };
          // v3.21-1796 · Team-recipient resolution now uses the
          // authoritative /sms-team-recipients endpoint introduced in
          // v1795 (canonical assignment IDs → current db.users records,
          // active + non-suspended, mobile probed from user record).
          const rec = await api
            .get(`/clients/${clientId}/sms-team-recipients`)
            .catch(() => ({ data: { recipients: [] } }));
          if (cancelled) return;
          setApplicationId(_appId);
          setOffers([_rawOffer]);       // seed with the clicked offer only
          setRequested(_requested);
          setResultType(_resultType);
          setChosenRank(1);             // clicked offer is always rank 1 of the seeded pool
          setMessageEdited(false);      // let the composer regenerate on hydration
          setNeedsAttentionFields([]);
          const recs = (rec?.data?.recipients || [])
            .filter(r => r?.recipient_type === 'team' && r?.selectable && r?.user_id);
          setRecipients(recs);
          setSelectedUids([]);
          setManualPhone('');
          setIncludeManual(false);
          return;
        }
        // Fallback path (surfaces that do NOT launch from a clicked
        // offer): fetch the application-scoped result.  Preserves v1776
        // behaviour for non-DealDashboard entry points.
        //
        // v3.21-1782 · Application-identity resolution here now uses
        // ONLY canonical Finturf pointers (`current_finturf_application_id`
        // and `deal_number`).  `client_external_id` was removed to
        // prevent the newly-editable Business Client ID from
        // accidentally resolving as a Finturf application identifier.
        //
        // v3.21-1796 · Recipient resolver switched to the
        // authoritative /sms-team-recipients endpoint.  When the
        // canonical /finturf endpoint returns nothing (Client with no
        // application yet, or the application record is not
        // reachable), the classifier still resolves via
        // `client.finturf_state` and — as a final fallback — defaults
        // to NOT_SUBMITTED so the modal remains sendable.
        const appId = client?.current_finturf_application_id
                   || client?.deal_number
                   || null;
        const url = appId ? `/finturf/applications/${appId}/offers`
                          : `/finturf/offers/${clientId}`;
        const [{ data: result }, rec] = await Promise.all([
          api.get(url).catch(() => ({ data: {} })),
          api.get(`/clients/${clientId}/sms-team-recipients`)
             .catch(() => ({ data: { recipients: [] } })),
        ]);
        if (cancelled) return;
        setApplicationId(result?.application_id || result?.identifier || appId);
        setOffers(Array.isArray(result?.offers) ? result.offers : []);
        setRequested(Number(result?.requested_amount || client?.requested_amount || client?.deal_amount || 0));
        setResultType(result?.result_type || client?.finturf_state || 'NOT_SUBMITTED');
        setNeedsAttentionFields(client?.finturf_needs_attention_fields || []);
        const recs = (rec?.data?.recipients || [])
          .filter(r => r?.recipient_type === 'team' && r?.selectable && r?.user_id);
        setRecipients(recs);
        // v1776 §3 — default to no auto-selection; operator explicitly picks.
        setSelectedUids([]);
        setManualPhone('');
        setIncludeManual(false);
      } catch (e) {
        toast.error('Could not load current financing result');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId, initialOffer, initialOfferNumber, initialApplicationId, initialResultType]);   // eslint-disable-line react-hooks/exhaustive-deps

  const { top: recommendations } = useMemo(
    () => pickTopTwoRecommendations(offers, requested),
    [offers, requested]
  );

  const chosenOffer = recommendations[chosenRank - 1] || null;

  const [messageEdited, setMessageEdited] = useState(false);
  useEffect(() => {
    if (messageEdited) return;
    // v3.21-1820 · Custom Message mode owns the textarea completely.
    // Auto-regeneration NEVER fires while custom mode is on — the
    // operator's text is preserved regardless of any concurrent
    // status refresh or classification churn.
    if (customMessageMode) return;
    // v3.21-1796 · Every canonical classifier state prepopulates a
    // provider-neutral message the operator can edit before sending.
    // VALID_OFFER retains the exact v3.21-1787 clicked-offer detail
    // hydration.  Non-offer states no longer leave the textarea blank
    // and no longer require a `chosenOffer` to become sendable.
    if (resultType === 'VALID_OFFER' && chosenOffer) {
      setMessage(buildValidOfferMessage(client, chosenOffer, applicationId, requested));
    } else if (resultType === 'NO_OFFER_FOUND') {
      setMessage(buildNoOfferMessage(client, applicationId));
    } else if (resultType === 'SUBMISSION_ERROR') {
      setMessage(buildSubmissionIssueMessage(client, applicationId));
    } else if (resultType === 'PROCESSING') {
      setMessage(buildProcessingMessage(client, applicationId));
    } else if (resultType === 'NEEDS_ATTENTION') {
      setMessage(buildNeedsAttentionMessage(client, applicationId));
    } else if (resultType === 'NOT_SUBMITTED') {
      setMessage(buildNotSubmittedMessage(client, applicationId));
    } else {
      setMessage('');
    }
  }, [resultType, chosenOffer, applicationId, requested, client, messageEdited, customMessageMode]);   // eslint-disable-line

  const toggleUid = (uid) => {
    setSelectedUids((cur) => cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid]);
  };
  const selectAll = () => setSelectedUids(recipients.map(r => r.user_id));
  const clearAll  = () => setSelectedUids([]);

  const totalSelected =
    selectedUids.length + ((includeManual && manualPhone.trim()) ? 1 : 0);

  // v3.21-1820 · Custom Message mode is fully independent of any
  // financing classification.  A non-empty message plus at least one
  // authorized recipient is sufficient, regardless of resultType
  // (including UNKNOWN, missing, or contradictory) and regardless of
  // chosenOffer.  Custom messaging NEVER depends on chosenOffer.
  //
  // Auto Message mode preserves the exact v3.21-1819 requirements:
  //   * VALID_OFFER requires chosenOffer (offer-detail template)
  //   * NO_OFFER_FOUND / SUBMISSION_ERROR / PROCESSING /
  //     NEEDS_ATTENTION / NOT_SUBMITTED sendable without an offer
  const canSend = customMessageMode
    ? true
    : (
        (resultType === 'VALID_OFFER' && !!chosenOffer)
        || resultType === 'NO_OFFER_FOUND'
        || resultType === 'SUBMISSION_ERROR'
        || resultType === 'PROCESSING'
        || resultType === 'NEEDS_ATTENTION'
        || resultType === 'NOT_SUBMITTED'
      );

  const sendDisabled = sending || loading || !canSend
    || totalSelected === 0
    || !message.trim();

  const send = async () => {
    if (sendDisabled) return;
    if (!message.trim()) { toast.error('Message cannot be empty'); return; }

    const payloadRecipients = [];
    for (const uid of selectedUids) {
      const rec = recipients.find(r => r.user_id === uid);
      if (rec) payloadRecipients.push({
        user_id: rec.user_id,
        label:   rec.full_name || rec.email || rec.role,
        manual:  false,
      });
    }
    if (includeManual && manualPhone.trim()) {
      payloadRecipients.push({
        user_id: null,
        phone:   manualPhone.trim(),
        label:   'Manual number',
        manual:  true,
      });
    }
    if (payloadRecipients.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }

    const payload = {
      client_id:    clientId,
      result_type:  customMessageMode ? 'CUSTOM' : resultType,
      message:      message.trim(),
      recipients:   payloadRecipients,
      is_custom:    !!customMessageMode,
    };
    if (!customMessageMode && resultType === 'VALID_OFFER' && chosenOffer) {
      payload.offer_snapshot = {
        approved_amount: chosenOffer.approved,
        term:            chosenOffer.term,
        apr:             chosenOffer.apr,
        monthly_payment: chosenOffer.payment,
        offer_id:        chosenOffer.offerId,
        application_id:  applicationId,
      };
    }

    setSending(true);
    setPerRecipientResult(null);
    try {
      const { data } = await api.post('/finturf/text-result/batch', payload);
      setPerRecipientResult(data);
      const sent = data?.sent || 0;
      const failed = data?.failed || 0;
      if (sent > 0 && failed === 0) toast.success(`${sent} sent`);
      else if (sent > 0 && failed > 0) toast.warning(`${sent} sent · ${failed} failed`);
      else toast.error(`${failed} failed`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Send failed'));
    } finally {
      setSending(false);
    }
  };

  const stateIcon = {
    VALID_OFFER:      <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
    NO_OFFER_FOUND:   <XCircle className="h-4 w-4 text-slate-500" />,
    SUBMISSION_ERROR: <AlertTriangle className="h-4 w-4 text-amber-600" />,
    NEEDS_ATTENTION:  <AlertTriangle className="h-4 w-4 text-amber-600" />,
  }[resultType] || <MessageSquare className="h-4 w-4 text-slate-400" />;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="financing-result-team-message-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {stateIcon}
            Text Financing Result to Team
            {applicationId && <span className="ml-2 text-[11px] font-mono text-slate-500">{applicationId}</span>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
        ) : resultType === 'NEEDS_ATTENTION' && !customMessageMode ? (
          <div className="space-y-3" data-testid="team-msg-needs-attention">
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <div className="font-semibold mb-1">Needs Attention — cannot send yet</div>
              {needsAttentionFields.length > 0 ? (
                <ul className="list-disc pl-5 text-[12px]">
                  {needsAttentionFields.map(f => (
                    <li key={f} data-testid={`needs-attention-field-${f}`}>{f}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-[12px]">Required information is missing. Correct it in Client Info and the automatic pipeline will resume.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {resultType === 'VALID_OFFER' && recommendations.length > 0 && (
              <div className="space-y-2" data-testid="team-msg-recommendation-selector">
                <div className="text-[11px] uppercase text-slate-500">Recommended offer to communicate</div>
                <div className="grid grid-cols-2 gap-2">
                  {recommendations.map((item, i) => (
                    <button
                      key={`rec-${item.originalIndex}`}
                      type="button"
                      onClick={() => { setChosenRank(i + 1); setMessageEdited(false); }}
                      className={`text-left rounded-lg border p-2 transition-colors ${chosenRank === i + 1
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:bg-slate-50'}`}
                      data-testid={`team-msg-select-recommended-${i + 1}`}
                    >
                      <div className="text-[10px] uppercase text-slate-500">Recommended #{i + 1}</div>
                      <div className="text-sm font-bold text-emerald-700">{fmtMoney(item.approved)}</div>
                      <div className="text-[11px] text-slate-600">
                        {item.term > 0 ? `${item.term} mo · ` : ''}
                        {item.apr > 0 ? `${fmtApr(item.apr)} · ` : ''}
                        {item.payment > 0 ? `${fmtMoneyD(item.payment)}/mo` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* v3.21-1776 · Multi-recipient checklist */}
            <div className="space-y-1" data-testid="team-msg-recipient-list">
              <div className="flex items-center justify-between">
                <label className="text-[11px] uppercase text-slate-500">Recipient(s)</label>
                <div className="flex gap-2 text-[11px]">
                  <button
                    type="button"
                    onClick={selectAll}
                    disabled={recipients.length === 0 || selectedUids.length === recipients.length}
                    className="text-blue-600 hover:underline disabled:opacity-40"
                    data-testid="team-msg-select-all"
                  >Select All</button>
                  <button
                    type="button"
                    onClick={clearAll}
                    disabled={selectedUids.length === 0}
                    className="text-slate-500 hover:underline disabled:opacity-40"
                    data-testid="team-msg-clear-all"
                  >Clear</button>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto border rounded divide-y">
                {recipients.length === 0 && (
                  <div className="p-2 text-xs text-slate-500">
                    (no authorized recipients on this deal)
                  </div>
                )}
                {recipients.map(r => (
                  <label
                    key={r.user_id}
                    className="flex items-center gap-2 p-1.5 text-xs hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUids.includes(r.user_id)}
                      onChange={() => toggleUid(r.user_id)}
                      data-testid={`team-msg-recipient-checkbox-${r.user_id}`}
                    />
                    <span className="flex-1">
                      <span className="font-medium">{r.full_name || r.email || r.role}</span>
                      {r.role && <span className="text-slate-500"> · {r.role}</span>}
                      {r.phone && <span className="text-slate-500 font-mono ml-1">{maskPhone(r.phone)}</span>}
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-[10px] text-slate-500">Selected: {totalSelected}</div>

              {/* v1776 §5 — Manual "Use Different Number" preserved */}
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  checked={includeManual}
                  onChange={(e) => setIncludeManual(e.target.checked)}
                  data-testid="team-msg-manual-toggle"
                />
                <span className="text-[11px] text-slate-600">Use Different Number</span>
                <input
                  type="tel"
                  placeholder="Manual override phone"
                  value={manualPhone}
                  onChange={(e) => setManualPhone(e.target.value)}
                  disabled={!includeManual}
                  className="flex-1 text-xs border rounded px-2 py-1 disabled:opacity-40"
                  data-testid="team-msg-manual-phone"
                />
              </div>
            </div>

            <div className="space-y-1">
              {/* v3.21-1820 · Custom Message mode toggle — decouples
                  authorized team messaging from financing classification
                  and chosenOffer entirely. */}
              <div className="flex items-center justify-between">
                <label className="text-[11px] uppercase text-slate-500" htmlFor="team-msg-body">Message</label>
                <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customMessageMode}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setCustomMessageMode(on);
                      if (on) {
                        // Switching INTO custom mode: mark as edited so
                        // any residual auto-regeneration is suppressed
                        // and preserve whatever the operator has typed.
                        setMessageEdited(true);
                      } else {
                        // Switching back to Auto mode: let the classifier
                        // template repopulate for the current resultType.
                        setMessageEdited(false);
                      }
                    }}
                    data-testid="team-msg-custom-mode-toggle"
                  />
                  <span data-testid="team-msg-custom-mode-label">Custom Message</span>
                </label>
              </div>
              <textarea
                id="team-msg-body"
                data-testid="team-msg-body"
                value={message}
                onChange={(e) => { setMessage(e.target.value); setMessageEdited(true); }}
                rows={6}
                className="w-full text-sm border rounded px-2 py-1.5 font-mono"
              />
              <div className="text-[10px] text-slate-400">
                State: <span className="font-semibold uppercase text-slate-600" data-testid="team-msg-state">{customMessageMode ? 'CUSTOM' : (resultType || '—')}</span>
              </div>
            </div>

            {perRecipientResult && Array.isArray(perRecipientResult.per_recipient_results) && (
              <div className="border rounded p-2 bg-slate-50 space-y-1" data-testid="team-msg-per-recipient-result">
                <div className="text-[11px] text-slate-500">
                  {perRecipientResult.sent} sent{perRecipientResult.failed > 0 ? ` · ${perRecipientResult.failed} failed` : ''}
                </div>
                {perRecipientResult.per_recipient_results.map((row, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 text-xs">
                    {row.ok
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      : <AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
                    <span>{row.label || row.phone_masked || 'Manual number'}</span>
                    <span className="text-slate-500">
                      — {row.ok ? 'Sent' : (row.reason === 'duplicate_destination' ? 'Duplicate (folded)' : (row.error || 'Failed'))}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="team-msg-cancel">Cancel</Button>
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={send}
            disabled={sendDisabled}
            data-testid="team-msg-send"
          >
            {sending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
            Send to Team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
